import React, { useEffect, useState } from "react"
import useAuth from "../../context/userContext"
import axios from "axios"
import api, { BASE_URL, fetcher } from "../../utils/api"
import DeleteModal from "../../components/DeleteModal"
import OTPModal from "../../components/modals/OTPModal"
import StatCard from "../../components/Cards/StatCard"
import DashboardOverviewFilter from "../../components/DashboardOverviewFilter"
import DashboardHeader from "../../components/DashboardHeader"
import SMSSenderModal from "../../components/modals/SMSSenderModal"
import OverviewFilter from "../../components/OverviewFilter"
import Spinner from "../../components/Spinner"
import ServiceOrderChart from "../../components/Carts/ServiceOrderChart"
import OrderPieChart from "../../components/Carts/OrderPieChart"
import { ErrorNotification } from "../../utils"
import useSWR from "swr"
import { isAccountOwnerEligibleForSenderIdPrompt } from "../../utils/smsSenderId"

const Dashboard = () => {
  const [analyticData, setAnalyticData] = useState()
  const [loading, setLoading] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [isFirstTime, setIsFirstTime] = useState(false)
  const token = localStorage.getItem("token")
  const { showSmsModal, setShowSmsModal, user: authUser } = useAuth()
  const [selectedFilter, setSelectedFilter] = useState("30 days")
  const [stats, setStats] = useState(null)
  const [loadingState, setLoadingState] = useState(true)
  const [localUser, setLocalUser] = useState(null)
  const [ordersCount, setOrdersCount] = useState(0)

  const user = authUser || localUser
  const verificationRequired =
    window.localStorage.getItem("verification_required") === "true"
  const needsFirstTimeOtp =
    user?.user_type !== "activity_user" &&
    verificationRequired &&
    (Boolean(user?.is_firsttime) || isFirstTime)


  const { data: harvestRequest, isLoading, mutate } = useSWR(
    `harvest-requests/`,
    fetcher
  )


  const getDashboardAnalytics = () => {
    setLoading(true)
    var config = {
      method: "get",
      url: `${BASE_URL}aggregator/dashboardAnalytics/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: "",
    }

    axios(config)
      .then(function (response) {
        setAnalyticData(response.data.data)
        setLoading(false)
      })
      .catch(function (error) {
        setLoading(false)
      })
  }

  const getStats = async () => {
    setLoadingState(true)
    try {
      const response = await api.get('farmers/statistics/')

      if (response.status === 200 && response.data) {
        setStats(response.data)
      }
    } catch (error) {
      ErrorNotification(error.response?.data || "Failed to get dashboard stats")
    } finally {
      setLoadingState(false)
    }
  }

  const getOrdersCount = async () => {
    try {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30) // Last 30 days

      const params = {
        page_size: 1000, // Get a large number to count all
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
      }

      const response = await api.get('service-orders/orders/', { params })

      // Get total count from response - check for count field first, then count results
      let totalCount = 0
      if (response.data?.count !== undefined) {
        totalCount = response.data.count
      } else if (Array.isArray(response.data?.results)) {
        totalCount = response.data.results.length
      } else if (Array.isArray(response.data)) {
        totalCount = response.data.length
      }

      setOrdersCount(totalCount)
    } catch (error) {
      console.error('Error fetching orders count:', error)
      // Keep the current count or set to 0
      setOrdersCount(prev => prev || 0)
    }
  }

  const handleDelete = (id) => {
    // let token = localStorage.getItem("token");
    setLoading(true)

    var data = JSON.stringify({
      id: id,
      item: "harvest",
    })

    var config = {
      method: "post",
      url: `${BASE_URL}aggregator/deleteItem`,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: data,
    }

    axios(config)
      .then(function (response) {
        setLoading(false)
      })
      .catch(function (error) {
        setLoading(false)
      })
  }


  useEffect(() => {
    const userData = JSON.parse(window.localStorage.getItem("user"))
    setLocalUser(userData)
    setIsFirstTime(Boolean(userData?.is_firsttime))
    getDashboardAnalytics()
    getStats()
    getOrdersCount()
  }, [])

  // Update ordersCount if analyticData has a valid count (as fallback)
  useEffect(() => {
    if (analyticData?.orders_count !== undefined && analyticData.orders_count > 0) {
      // Use analyticData count if we don't have a fetched count yet
      if (ordersCount === 0) {
        setOrdersCount(analyticData.orders_count)
      }
    }
  }, [analyticData])

  return (
    <>
      {showConfirmModal && <DeleteModal
        setShowConfirmModal={setShowConfirmModal}
        showConfirmModal={showConfirmModal}
        title="harvest"
        handleDelete={() => handleDelete(selected)}
      />}
      
      <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10 space-y-6">
        <DashboardHeader label="Dashboard" />
        
        {/* First-time account OTP: resend-otp + verify-otp */}
        {needsFirstTimeOtp && (
          <OTPModal isOpen={true} setIsOpen={() => { }} dismissible={false} />
        )}

        {isAccountOwnerEligibleForSenderIdPrompt(user) && showSmsModal && (
          <SMSSenderModal isOpen={showSmsModal} setIsOpen={setShowSmsModal} />
        )}
        {/* Dashboard filter */}
        {/* <DashboardOverviewFilter
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        /> */}


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard title={"Total Farmers"} count={stats?.total_farmers || 0} />
          <StatCard title={"Total Farms"} count={stats?.total_farms || 0} />
          <StatCard title={"Total Requests"} count={harvestRequest?.length || 0} />
          <StatCard title={"Total Sales"} count={ordersCount || analyticData?.orders_count || 0} />
        </div>

        <OverviewFilter
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />

        {/* <CommodityPrices /> */}

        {loading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <>
            {/* charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className={`${analyticData?.harvestChartData?.dataset ? "col-span-2" : "col-span-3"}`}>
                <ServiceOrderChart data={{
                  dataset: {
                    label: analyticData?.orderChartData?.labels,
                    data: analyticData?.orderChartData?.datasets,
                  }
                  }} />
                </div>
              </div>
              {/* harvest */}
              {/* <RecentHarvestList harvests={analyticData?.recent_harvests || []} /> */}
            </>
        )}

      </div>
    </>
  )
}


export default Dashboard
